import AsyncStorage from "@react-native-async-storage/async-storage";
import * as Crypto from "expo-crypto";
import { httpsCallable } from "firebase/functions";

import { listingPaymentFunctions } from "@/lib/firebase";
import { parseListingPaymentConfiguration } from "@/lib/listingPaymentConfiguration";
import {
  createListingPaymentIntent,
  isValidListingPaymentIdempotencyKey,
  listingPaymentIntentStorageKey,
  parseListingPaymentIntent,
} from "@/lib/listingPaymentIntent";
import {
  ListingPaymentProtocolError,
  parseCreatePaidListingDraftResponse,
  parseRecoverCurrentListingPaymentResponse,
} from "@/lib/listingPaymentProtocol";
import {
  LISTING_PAYMENT_STATUSES,
  type CreatePaidListingDraftRequest,
  type CreatePaidListingDraftResponse,
  type GetListingPaymentConfigurationRequest,
  type GetListingPaymentStatusRequest,
  type GetListingPaymentStatusResponse,
  type InitiateListingPaymentRequest,
  type InitiateListingPaymentResponse,
  type ListingPaymentIntentReadResult,
  type ListingPaymentConfiguration,
  type ListingPaymentStatus,
  type PersistedListingPaymentIntent,
  type RecoverCurrentListingPaymentRequest,
  type RecoverCurrentListingPaymentResponse,
} from "@/types/listingPayment";

export {
  isStalePaymentConfigurationError,
  STALE_PAYMENT_CONFIGURATION_MESSAGE,
} from "@/lib/listingPaymentInitiationError";

const LEGACY_PAYMENT_SESSION_STORAGE_PREFIX = "sokogrid:listing-payment:v1";
const IDEMPOTENCY_KEY_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;
const PAYMENT_ID_PATTERN = /^pay_[a-f0-9]{40}$/;
const ENTITLEMENT_ID_PATTERN = /^ent_pay_[a-f0-9]{40}$/;

const initiatePaymentCallable = httpsCallable<
  InitiateListingPaymentRequest,
  InitiateListingPaymentResponse
>(listingPaymentFunctions, "initiateListingPayment");

const getPaymentConfigurationCallable = httpsCallable<
  GetListingPaymentConfigurationRequest,
  ListingPaymentConfiguration
>(listingPaymentFunctions, "getListingPaymentConfiguration");

const getPaymentStatusCallable = httpsCallable<
  GetListingPaymentStatusRequest,
  GetListingPaymentStatusResponse
>(listingPaymentFunctions, "getListingPaymentStatus");

const recoverCurrentPaymentCallable = httpsCallable<
  RecoverCurrentListingPaymentRequest,
  RecoverCurrentListingPaymentResponse
>(listingPaymentFunctions, "recoverCurrentListingPayment");

const createPaidDraftCallable = httpsCallable<
  CreatePaidListingDraftRequest,
  CreatePaidListingDraftResponse
>(listingPaymentFunctions, "createPaidListingDraft");

export { ListingPaymentProtocolError } from "@/lib/listingPaymentProtocol";

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

export function isListingPaymentStatus(
  value: unknown
): value is ListingPaymentStatus {
  return LISTING_PAYMENT_STATUSES.includes(value as ListingPaymentStatus);
}

export function isActiveListingPaymentStatus(
  status: ListingPaymentStatus
): boolean {
  return status === "created" || status === "initiating" || status === "pending";
}

export function isFailedListingPaymentStatus(
  status: ListingPaymentStatus
): status is "failed" | "cancelled" | "timed_out" {
  return status === "failed" || status === "cancelled" || status === "timed_out";
}

export function isValidListingPaymentId(value: unknown): value is string {
  return typeof value === "string" && PAYMENT_ID_PATTERN.test(value);
}

export function isValidListingEntitlementId(value: unknown): value is string {
  return typeof value === "string" && ENTITLEMENT_ID_PATTERN.test(value);
}

export function generateListingPaymentIdempotencyKey(): string {
  const key = Crypto.randomUUID();

  if (!IDEMPOTENCY_KEY_PATTERN.test(key)) {
    throw new ListingPaymentProtocolError(
      "A secure payment request key could not be created."
    );
  }

  return key;
}

async function ownerScopeHash(uid: string) {
  if (!uid.trim()) {
    throw new ListingPaymentProtocolError(
      "A signed-in owner is required for local payment intent storage."
    );
  }

  return Crypto.digestStringAsync(
    Crypto.CryptoDigestAlgorithm.SHA256,
    `sokogrid-listing-payment-owner:${uid}`
  );
}

function legacySessionStorageKey(uid: string) {
  return `${LEGACY_PAYMENT_SESSION_STORAGE_PREFIX}:${uid}`;
}

export async function readListingPaymentIntent(
  uid: string,
  nowMs = Date.now()
): Promise<ListingPaymentIntentReadResult> {
  const scopeHash = await ownerScopeHash(uid);
  const storageKey = listingPaymentIntentStorageKey(scopeHash);
  const [raw, storageKeys] = await Promise.all([
    AsyncStorage.getItem(storageKey),
    AsyncStorage.getAllKeys(),
  ]);
  const legacySessionPresent = storageKeys.includes(
    legacySessionStorageKey(uid)
  );

  if (!raw) {
    return { status: "missing", intent: null, legacySessionPresent };
  }

  try {
    const parsed = parseListingPaymentIntent({
      value: JSON.parse(raw),
      expectedOwnerScopeHash: scopeHash,
      nowMs,
    });

    return { ...parsed, legacySessionPresent };
  } catch {
    return { status: "malformed", intent: null, legacySessionPresent };
  }
}

export async function saveListingPaymentIntent(args: {
  uid: string;
  idempotencyKey: string;
  nowMs?: number;
}): Promise<PersistedListingPaymentIntent> {
  if (!isValidListingPaymentIdempotencyKey(args.idempotencyKey)) {
    throw new ListingPaymentProtocolError(
      "The local payment intent could not be saved safely."
    );
  }

  const scopeHash = await ownerScopeHash(args.uid);
  const intent = createListingPaymentIntent({
    ownerScopeHash: scopeHash,
    idempotencyKey: args.idempotencyKey,
    nowMs: args.nowMs ?? Date.now(),
  });

  await AsyncStorage.setItem(
    listingPaymentIntentStorageKey(scopeHash),
    JSON.stringify(intent)
  );

  return intent;
}

export async function clearListingPaymentIntent(uid: string) {
  const scopeHash = await ownerScopeHash(uid);
  await AsyncStorage.removeItem(listingPaymentIntentStorageKey(scopeHash));
}

export async function clearLegacyListingPaymentSession(uid: string) {
  if (!uid.trim()) return;
  await AsyncStorage.removeItem(legacySessionStorageKey(uid));
}

export async function clearAllListingPaymentLocalState(uid: string) {
  const scopeHash = await ownerScopeHash(uid);
  await AsyncStorage.multiRemove([
    listingPaymentIntentStorageKey(scopeHash),
    legacySessionStorageKey(uid),
  ]);
}

function requireInitiationResponse(value: unknown): InitiateListingPaymentResponse {
  if (
    !isRecord(value) ||
    !isValidListingPaymentId(value.paymentId) ||
    !isListingPaymentStatus(value.status)
  ) {
    throw new ListingPaymentProtocolError();
  }

  return { paymentId: value.paymentId, status: value.status };
}

function requireStatusResponse(
  requestedPaymentId: string,
  value: unknown
): GetListingPaymentStatusResponse {
  if (
    !isRecord(value) ||
    value.paymentId !== requestedPaymentId ||
    !isValidListingPaymentId(value.paymentId) ||
    !isListingPaymentStatus(value.status) ||
    typeof value.ownerId !== "string" ||
    !value.ownerId.trim() ||
    value.planId !== "basic_business_listing_annual" ||
    value.planName !== "Basic Business Listing" ||
    !(
      (value.amount === 5000 &&
        (value.canonicalPlanAmount === undefined ||
          (value.canonicalPlanAmount === 5000 &&
            (value.environment === "sandbox" ||
              value.environment === "production") &&
            value.isSandboxTestPayment === false))) ||
      (value.amount === 1 &&
        value.canonicalPlanAmount === 5000 &&
        ((value.environment === "sandbox" &&
          value.isSandboxTestPayment === true) ||
          (value.environment === "production" &&
            value.isSandboxTestPayment === false)))
    ) ||
    value.currency !== "KES" ||
    value.method !== "mpesa_paybill_stk_push" ||
    typeof value.maskedPhone !== "string" ||
    !value.maskedPhone.trim()
  ) {
    throw new ListingPaymentProtocolError();
  }

  if (
    value.entitlementId !== undefined &&
    !isValidListingEntitlementId(value.entitlementId)
  ) {
    throw new ListingPaymentProtocolError();
  }

  return value as GetListingPaymentStatusResponse;
}

export async function initiateListingPayment(
  input: InitiateListingPaymentRequest
): Promise<InitiateListingPaymentResponse> {
  if (
    !isValidListingPaymentIdempotencyKey(input.idempotencyKey) ||
    typeof input.phoneNumber !== "string" ||
    !input.phoneNumber.trim() ||
    !Number.isSafeInteger(input.configurationVersion) ||
    input.configurationVersion <= 0 ||
    !/^[a-f0-9]{64}$/.test(input.configurationFingerprint)
  ) {
    throw new ListingPaymentProtocolError(
      "The saved payment request is not valid."
    );
  }

  const result = await initiatePaymentCallable({
    phoneNumber: input.phoneNumber,
    idempotencyKey: input.idempotencyKey,
    configurationVersion: input.configurationVersion,
    configurationFingerprint: input.configurationFingerprint,
  });

  return requireInitiationResponse(result.data);
}

export async function getListingPaymentConfiguration(): Promise<ListingPaymentConfiguration> {
  const result = await getPaymentConfigurationCallable({});
  return parseListingPaymentConfiguration(result.data);
}

export async function getListingPaymentStatus(
  input: GetListingPaymentStatusRequest
): Promise<GetListingPaymentStatusResponse> {
  if (!isValidListingPaymentId(input.paymentId)) {
    throw new ListingPaymentProtocolError();
  }

  const result = await getPaymentStatusCallable({ paymentId: input.paymentId });
  return requireStatusResponse(input.paymentId, result.data);
}

export async function recoverCurrentListingPayment(): Promise<RecoverCurrentListingPaymentResponse> {
  const result = await recoverCurrentPaymentCallable({});
  return parseRecoverCurrentListingPaymentResponse(result.data);
}

export async function createPaidListingDraft(
  input: CreatePaidListingDraftRequest
): Promise<CreatePaidListingDraftResponse> {
  if (!isValidListingEntitlementId(input.entitlementId)) {
    throw new ListingPaymentProtocolError(
      "The verified listing entitlement is not valid."
    );
  }

  const result = await createPaidDraftCallable({
    entitlementId: input.entitlementId,
    ownerName: input.ownerName,
    businessName: input.businessName,
    mobileNumber: input.mobileNumber,
  });

  return parseCreatePaidListingDraftResponse(
    input.entitlementId,
    result.data
  ) as CreatePaidListingDraftResponse;
}
